import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { CandidatePage, CandidateStatus, ProspectCandidate, ProspectingRun, ProspectingUsage } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

export class ApiError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    const message = p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code)
  }
  return res.json()
}

const base = (workspaceId: string) => `/api/workspaces/${workspaceId}/prospecting`

export function listCandidates(
  workspaceId: string,
  status: CandidateStatus,
  cursor?: string | null,
): Promise<CandidatePage> {
  const params = new URLSearchParams({ status, limit: '50' })
  if (cursor) params.set('cursor', cursor)
  return apiFetch('GET', `${base(workspaceId)}/candidates?${params.toString()}`)
}

function candidateAction(workspaceId: string, candidateId: string, action: string, body: unknown = {}): Promise<ProspectCandidate> {
  return apiFetch<{ candidate: ProspectCandidate }>(
    'POST',
    `${base(workspaceId)}/candidates/${encodeURIComponent(candidateId)}/${action}`,
    body,
  ).then((r) => r.candidate)
}

export function acceptCandidate(workspaceId: string, candidateId: string): Promise<ProspectCandidate> {
  return candidateAction(workspaceId, candidateId, 'accept')
}

export function rejectCandidate(workspaceId: string, candidateId: string, reason?: string): Promise<ProspectCandidate> {
  return candidateAction(workspaceId, candidateId, 'reject', reason ? { reason } : {})
}

export function approveCandidateCrm(workspaceId: string, candidateId: string): Promise<ProspectCandidate> {
  return candidateAction(workspaceId, candidateId, 'crm/approve')
}

export function dismissCandidateCrm(workspaceId: string, candidateId: string): Promise<ProspectCandidate> {
  return candidateAction(workspaceId, candidateId, 'crm/dismiss')
}

export function startRun(workspaceId: string): Promise<ProspectingRun> {
  return apiFetch<{ run: ProspectingRun }>('POST', `${base(workspaceId)}/runs`, {}).then((r) => r.run)
}

export function getRun(workspaceId: string, runId: string): Promise<ProspectingRun> {
  return apiFetch<{ run: ProspectingRun }>('GET', `${base(workspaceId)}/runs/${encodeURIComponent(runId)}`).then((r) => r.run)
}

export function getLatestRun(workspaceId: string): Promise<ProspectingRun | null> {
  return apiFetch<{ runs: ProspectingRun[] }>('GET', `${base(workspaceId)}/runs?limit=1`).then((r) => r.runs[0] ?? null)
}

/** Owner/Admin only — members get 403. */
export function getUsage(workspaceId: string): Promise<ProspectingUsage> {
  return apiFetch<{ usage: ProspectingUsage }>('GET', `${base(workspaceId)}/usage`).then((r) => r.usage)
}
